import { MarketIDMap } from "@app/_shared/shared.enums";
import { MarketWatch } from "@app/app.models";
import { MarketKeywords, Instrument, Series, OptionTypeEnum } from "@app/app.structs";

export namespace ChartUtilities {

    export function getAtp(value: number, volume: number, trade_value: number, market_lot: number, market_id: string) {
        if (market_id === MarketKeywords.NSECDS || market_id === MarketKeywords.BSECDS) {
            return value / (volume * market_lot * trade_value);
        }
        return value / (volume * trade_value);
    }

    function getDateString(data: string) {
        data = data.split('-').join('');
        return data;
    }

    /**
     * Translates a TradingView timestamp to MDL (Market Data Link) timestamp
     * @param epochTime The timestamp in seconds since epoch (Unix timestamp)
     * @returns The translated timestamp in seconds, adjusted for IST (India Standard Time)
     */
    export function translateTVTimestampForMdl(epochTime: number) {
        // Convert epoch seconds to milliseconds and create a Date object
        let date = new Date(epochTime * 1000);
        
        // Adjust for IST (UTC+5:30)
        date.setHours(date.getHours() + 5);
        date.setMinutes(date.getMinutes() + 30);
        
        // Uncomment the following line if seconds adjustment is needed
        // date.setSeconds(date.getSeconds() + 30);
        
        // Convert back to seconds and return
        return date.getTime() / 1000;
    }

    export function getInstrumentName(symbol: MarketWatch) {
        let instrument_name = `DEFAULT ${MarketIDMap[symbol.market_id]} ${symbol.symbol}`;
        const instrument_type = symbol.instrument_type;
        if (Instrument[instrument_type] === 'common_stock') {
            if (((symbol.market_id !== MarketKeywords.BSECM) && (symbol.series !== Series.A)) ||
                ((symbol.market_id !== MarketKeywords.NSECM) && (symbol.series !== Series.EQ))) {
                instrument_name += ' ' + symbol.series;
            }
        }

        const expiry = symbol.expiry_date.split('T')[0];
        if (instrument_type === 'F') {
            instrument_name += ' ' + getDateString(expiry);
            // To handle the case of spread, straddle and paired
            if (symbol.expiry_date2) {
                instrument_name += ' ' + getDateString(symbol.expiry_date2);
            }
        }
        if (instrument_type === 'O') {
            const optionType = symbol.option_type;
            const strikePrice = symbol.strike_price * Math.pow(10, symbol.scrip_precision);
            instrument_name += ' ' + getDateString(expiry) + ' ' + symbol.strike_price + ' ' + optionType;
        }
        return instrument_name;
    }
    /**
     * Processes market rules to extract session holidays and corrections
     * @param marketData Object containing market rules
     * @returns Object with arrays of session_holidays and corrections
     */
    export function processMarketRules(marketData) {
        const result = {
            session_holidays: [],
            corrections: []
        };

        // Check if marketData has rules and it's an array
        if (marketData.rules && Array.isArray(marketData.rules)) {
            marketData.rules.forEach(rule => {
                if (rule.date) {
                    if (rule.open && rule.close) {
                        // This is a correction (special trading session)
                        const correctionDate = rule.date.replace(/-/g, ''); // Remove hyphens from date
                        const correctionTime = `${rule.open.replace(':', '')}-${rule.close.replace(':', '')}`; // Format time range
                        result.corrections.push(`${correctionTime}:${correctionDate}`);
                    } else {
                        // This is a session holiday (full day off)
                        result.session_holidays.push(rule.date.replace(/-/g, '')); // Remove hyphens from date
                    }
                }
            });
        }

        // Join corrections and holidays into a single string
        result.corrections.join(';');
        result.session_holidays.join(',');

        return result;
    }

    // Mapping markets mentioned in holiday file to the ones used in the app
    export function defaultMarketMap(marketHoursObj) {
        return {
            "NSECM": marketHoursObj.NSE,
            "BSECM": marketHoursObj.BOM,
            "NSEFO": marketHoursObj.NSE,
            "BSEFO": marketHoursObj.BOM,
            "BSECDS": marketHoursObj.BOM,
            "NSECDS": marketHoursObj.NSECD,
            "MCX": marketHoursObj.MCX
        }
    }
}